<template>
  <div class="relative">
    <FormLabel :for="name || id" :label="label" :required="required" :label-hint="labelHint"/>
    <textarea
      ref="inputEl"
      :id="id"
      :value="formatted"
      :placeholder="placeholder"
      :required="required"
      @paste="enforceFormat"
      @keydown="enforceFormat"
      @input="emitEvent('update:modelValue', $event)"
      @change="emitEvent('change', $event)"
      class="w-full px-4 py-2.5 leading-tight border rounded-lg appearance-none text-slate-900 bg-slate-100 focus:outline-none"
      :style="'height:' + defaultHeight + 'px;'"
      :class="textareaClasses"
      :aria-invalid="error ? 'true' : 'false'"
      :aria-describedby="error ? `${id}-error` : null"
      :readonly="readonly"
      :name="name"
    />
    <p v-if="error" :id="`${id}-error`" class="absolute left-0 text-sm text-red-600 bottom-1">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import FormLabel from "./FormLabel.vue";
import {computed, ref, watch} from "vue";
import {useInputFormatter} from "../composables/InputFormatter.js";

const props = defineProps({
  customId: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    default: "",
  },
  labelHint: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  modelValue: {
    type: [String, Number],
    default: "",
  },
  required: {
    type: Boolean,
    default: false,
  },
  defaultHeight: {
    type: Number,
    default: 128,
  },
  error: {
    type: String,
    default: "",
  },
  maxLength: {
    type: Number,
    default: 512,
  },
  resizableX: {
    type: Boolean,
    default: false,
  },
  resizableY: {
    type: Boolean,
    default: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  name: String,
});

const id = props.customId || "textarea-" + Math.random().toString(36).substr(2, 9);
const inputEl = ref(null);

const formatter = useInputFormatter("maxLength", {
  maxLength: props.maxLength,
});
const emits = defineEmits(["update:modelValue", "change"]);

const cleaned = ref(props.modelValue);
const formatted = computed(() => {
  return formatter.applyFormat(cleaned.value);
});

const enforceFormat = (e) =>
  formatter.enforceFormat(e, cleaned.value ? cleaned.value.length : 0);
const emitEvent = (emitName, e) => {
  cleaned.value = formatter.clean(e.target.value);
  emits(emitName, cleaned.value);
};
watch(
  () => props.modelValue,
  () => (cleaned.value = props.modelValue)
);

const textareaClasses = computed(() => [
  props.error ? "border-red-500 focus:shadow-outline shadow" : "",
  props.resizableY
    ? props.resizableX
      ? "resize"
      : "resize-y"
    : props.resizableX
      ? "resize-x"
      : "resize-none",
]);
</script>
